import React, { useState, useEffect, useMemo, useRef } from 'react';
import stepColorsData from '../data/stepColors.json';
import { convertExampleData } from '../utils/languageConverter';
import { generateComposition, refineComposition } from '../services/deepseekApi';
import { saveExampleToSupabase } from '../services/supabaseService';
import englishWritingDataService from '../services/englishDataService';
import supabase from '../services/supabaseClient';
import supabaseConnectionStatus from '../services/supabaseConnectionStatus';
import { NotificationManager, showNotification } from './Notification';
import ConnectionStatusIndicator from './ConnectionStatusIndicator';
import AIDrillInput from './AIDrillInput';
import AIDrillProgress from './AIDrillProgress';
import VoiceControls from './VoiceControls';
import PhoneticDisplay from './PhoneticDisplay';
import CompositionComposer from './CompositionComposer';
import '../styles/SentenceExpansionApp.css';

const SentenceExpansionApp = () => {
  // Mode: 'sentence' or 'composition'
  const [mode, setMode] = useState('sentence');
  
  const [currentStep, setCurrentStep] = useState(0);
  const [currentExample, setCurrentExample] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('english');
  const [allExamples, setAllExamples] = useState([]);
  const [filteredExamples, setFilteredExamples] = useState([]);
  const [languageData, setLanguageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  
  // Category data state
  const [natureExamples, setNatureExamples] = useState([]);
  const [animalsExamples, setAnimalsExamples] = useState([]);
  const [objectsExamples, setObjectsExamples] = useState([]);
  const [emotionsExamples, setEmotionsExamples] = useState([]);
  const [userDefinedExamples, setUserDefinedExamples] = useState([]);
  
  // AI Drill states
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiProgress, setAiProgress] = useState(null);
  const [currentAIGeneratedExample, setCurrentAIGeneratedExample] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  
  // Composition states
  const [currentComposition, setCurrentComposition] = useState(null);
  const [isGeneratingComposition, setIsGeneratingComposition] = useState(false);
  const [compositionProgress, setCompositionProgress] = useState(null);
  const [originalCompositionData, setOriginalCompositionData] = useState(null); // Store original data for saving
  const [isSavingComposition, setIsSavingComposition] = useState(false);
  const [compositionSaveMessage, setCompositionSaveMessage] = useState(null);
  
  // Refinement states
  const [refinementLevel, setRefinementLevel] = useState('primary');
  const [refinementStage, setRefinementStage] = useState('year5-6');
  const [isRefining, setIsRefining] = useState(false);
  const [refinementProgress, setRefinementProgress] = useState(null);
  const [refinedComposition, setRefinedComposition] = useState(null);
  
  // Interactive step selection states
  const [selectedSteps, setSelectedSteps] = useState(new Set());
  const [isInteractiveMode, setIsInteractiveMode] = useState(false);

  const stepColors = stepColorsData;
  const currentLanguageData = languageData?.[selectedLanguage] || {};
  
  // Monitor Supabase connection status
  useEffect(() => {
    let previousStatus = null;
    
    const unsubscribe = supabaseConnectionStatus.subscribe((status) => {
      // Show notification when status changes
      if (previousStatus !== null && previousStatus.status !== status.status) {
        if (status.status === 'connected') {
          showNotification('Successfully connected to Supabase', 'success', 3000);
        } else if (status.status === 'disconnected') {
          showNotification('Lost connection to Supabase, attempting to reconnect...', 'error', 5000);
        }
      }
      previousStatus = status;
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Fetch data from Supabase on component mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setLoadingError(null);
      
      // Always log (even in production) for debugging
      console.log('[SentenceExpansionApp] Starting data load...');
      
      try {
        // Fetch language data
        console.log('[SentenceExpansionApp] Fetching language data...');
        const langData = await englishWritingDataService.getLanguageData();
        setLanguageData(langData);
        console.log('[SentenceExpansionApp] Language data loaded');
        
        // Fetch examples by category in parallel using A2A service
        console.log('[SentenceExpansionApp] Fetching examples by category via A2A...');
        const [nature, animals, objects, emotions, userDefined] = await Promise.all([
          englishWritingDataService.getWritingExamplesByCategory('Nature'),
          englishWritingDataService.getWritingExamplesByCategory('Animals'),
          englishWritingDataService.getWritingExamplesByCategory('Objects'),
          englishWritingDataService.getWritingExamplesByCategory('Emotions'),
          englishWritingDataService.getWritingExamplesByCategory('UserDefined')
        ]);
        
        console.log('[SentenceExpansionApp] Examples loaded:', {
          nature: Array.isArray(nature) ? nature.length : 0,
          animals: Array.isArray(animals) ? animals.length : 0,
          objects: Array.isArray(objects) ? objects.length : 0,
          emotions: Array.isArray(emotions) ? emotions.length : 0,
          userDefined: Array.isArray(userDefined) ? userDefined.length : 0
        });
        
        setNatureExamples(nature);
        setAnimalsExamples(animals);
        setObjectsExamples(objects);
        setEmotionsExamples(emotions);
        setUserDefinedExamples(userDefined);
        
        // Combine all examples with category information
        const combined = [
          ...nature.map(item => ({ 
            ...convertExampleData(item, selectedLanguage), 
            category: langData[selectedLanguage]?.nature || 'Nature'
          })),
          ...animals.map(item => ({ 
            ...convertExampleData(item, selectedLanguage), 
            category: langData[selectedLanguage]?.animals || 'Animals'
          })),
          ...objects.map(item => ({ 
            ...convertExampleData(item, selectedLanguage), 
            category: langData[selectedLanguage]?.objects || 'Objects'
          })),
          ...emotions.map(item => ({ 
            ...convertExampleData(item, selectedLanguage), 
            category: langData[selectedLanguage]?.emotions || 'Emotions'
          })),
          ...userDefined.map(item => ({ 
            ...convertExampleData(item, selectedLanguage), 
            category: 'UserDefined'
          }))
        ];
        
        setAllExamples(combined);
        setFilteredExamples(combined);
        
        // Debug: Log the data structure
        if (combined.length > 0) {
          console.log('Loaded examples:', combined.length);
          console.log('First example structure:', combined[0]);
          console.log('First example steps:', combined[0]?.steps?.length || 0);
        }
        
        // Set initial category
        if (!selectedCategory && langData[selectedLanguage]) {
          setSelectedCategory(langData[selectedLanguage].allCategories);
        }
      } catch (error) {
        console.error('Error loading data from Supabase:', error);
        setLoadingError('Failed to load data. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []); // Only run on mount

  // Update examples when language changes
  useEffect(() => {
    if (!languageData || !currentLanguageData) return;
    
    // Combine all examples with category information and convert language
    const combined = [
      ...natureExamples.map(item => ({ 
        ...convertExampleData(item, selectedLanguage), 
        category: currentLanguageData.nature || 'Nature'
      })),
      ...animalsExamples.map(item => ({ 
        ...convertExampleData(item, selectedLanguage), 
        category: currentLanguageData.animals || 'Animals'
      })),
      ...objectsExamples.map(item => ({ 
        ...convertExampleData(item, selectedLanguage), 
        category: currentLanguageData.objects || 'Objects'
      })),
      ...emotionsExamples.map(item => ({ 
        ...convertExampleData(item, selectedLanguage), 
        category: currentLanguageData.emotions || 'Emotions'
      })),
      ...userDefinedExamples.map(item => ({ 
        ...convertExampleData(item, selectedLanguage), 
        category: 'UserDefined'
      }))
    ];
    setAllExamples(combined);
    setFilteredExamples(combined);
    
    // Set initial category
    if (!selectedCategory) {
      setSelectedCategory(currentLanguageData.allCategories);
    }
  }, [selectedLanguage, languageData, natureExamples, animalsExamples, objectsExamples, emotionsExamples, userDefinedExamples]);

  // Build categories array dynamically using useMemo to prevent unnecessary re-renders
  const categories = useMemo(() => {
    if (!languageData || !currentLanguageData) return [];
    return [
      { name: currentLanguageData.allCategories, data: [] },
      { name: currentLanguageData.nature, data: natureExamples },
      { name: currentLanguageData.animals, data: animalsExamples },
      { name: currentLanguageData.objects, data: objectsExamples },
      { name: currentLanguageData.emotions, data: emotionsExamples },
      { name: 'UserDefined', data: userDefinedExamples }
    ];
  }, [languageData, currentLanguageData, natureExamples, animalsExamples, objectsExamples, emotionsExamples, userDefinedExamples]);

  // Track previous category to only reset when it actually changes
  const prevCategoryRef = useRef(selectedCategory);
  const prevLanguageRef = useRef(selectedLanguage);
  
  useEffect(() => {
    if (!currentLanguageData.allCategories) return;
    
    const categoryChanged = prevCategoryRef.current !== selectedCategory;
    const languageChanged = prevLanguageRef.current !== selectedLanguage;
    
    if (selectedCategory === currentLanguageData.allCategories) {
      setFilteredExamples(allExamples);
    } else {
      const categoryData = categories.find(cat => cat.name === selectedCategory)?.data || [];
      setFilteredExamples(categoryData.map(item => ({ 
        ...convertExampleData(item, selectedLanguage), 
        category: selectedCategory 
      })));
    }
    
    // Only reset example and step when category or language actually changes
    if (categoryChanged || languageChanged) {
      setCurrentExample(0);
      setCurrentStep(0);
      prevCategoryRef.current = selectedCategory;
      prevLanguageRef.current = selectedLanguage;
    }
  }, [selectedCategory, selectedLanguage, allExamples, currentLanguageData, categories]);

  // Ensure currentExample is within bounds
  const safeCurrentExample = Math.min(currentExample, Math.max(0, filteredExamples.length - 1));
  const currentExampleData = filteredExamples[safeCurrentExample] || {};
  const steps = (currentExampleData && currentExampleData.steps && Array.isArray(currentExampleData.steps)) 
    ? currentExampleData.steps 
    : [];

  // Ensure currentStep is within bounds
  const safeCurrentStep = Math.min(currentStep, Math.max(0, steps.length - 1));
  const currentStepData = steps[safeCurrentStep] || { label: '', content: '' };

  const nextStep = () => {
    console.log('nextStep called - currentStep:', currentStep, 'steps.length:', steps.length);
    const currentSafeStep = Math.min(currentStep, Math.max(0, steps.length - 1));
    console.log('currentSafeStep:', currentSafeStep, 'can advance?', currentSafeStep < steps.length - 1);
    if (steps.length > 0 && currentSafeStep < steps.length - 1) {
      const newStep = currentSafeStep + 1;
      console.log('Setting currentStep to:', newStep);
      setCurrentStep(newStep);
    } else {
      console.log('Cannot advance - steps.length:', steps.length, 'currentSafeStep:', currentSafeStep);
    }
  };

  const prevStep = () => {
    console.log('prevStep called - currentStep:', currentStep, 'steps.length:', steps.length);
    const currentSafeStep = Math.min(currentStep, Math.max(0, steps.length - 1));
    console.log('currentSafeStep:', currentSafeStep, 'can go back?', currentSafeStep > 0);
    if (steps.length > 0 && currentSafeStep > 0) {
      const newStep = currentSafeStep - 1;
      console.log('Setting currentStep to:', newStep);
      setCurrentStep(newStep);
    } else {
      console.log('Cannot go back - currentSafeStep:', currentSafeStep);
    }
  };

  const resetSteps = () => {
    setCurrentStep(0);
  };

  // Save AI-generated example to Supabase
  const handleSaveExample = async () => {
    if (!currentAIGeneratedExample) {
      setSaveMessage({ type: 'error', text: 'No AI-generated example to save' });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      const result = await saveExampleToSupabase(currentAIGeneratedExample);
      
      if (result.success) {
        setSaveMessage({ type: 'success', text: result.message });
        
        // Reload UserDefined examples to include the newly saved one
        try {
          const updatedUserDefined = await englishWritingDataService.getWritingExamplesByCategory('UserDefined');
          setUserDefinedExamples(updatedUserDefined);
        } catch (error) {
          console.error('Error refreshing UserDefined examples:', error);
        }
        
        // Update the example in the list to show it's saved
        const updatedExamples = filteredExamples.map((example, index) => {
          if (index === currentExample && example.category === 'AI Generated') {
            return {
              ...example,
              category: 'UserDefined',
              saved: true
            };
          }
          return example;
        });
        setFilteredExamples(updatedExamples);
        
        // Update allExamples to include the new saved example
        const updatedAllExamples = allExamples.map((example, index) => {
          if (index === currentExample && example.category === 'AI Generated') {
            return {
              ...example,
              category: 'UserDefined',
              saved: true
            };
          }
          return example;
        });
        setAllExamples(updatedAllExamples);
        
        // Clear the current AI example after successful save
        setCurrentAIGeneratedExample(null);
      }
    } catch (error) {
      console.error('Error saving example:', error);
      setSaveMessage({ type: 'error', text: `Failed to save: ${error.message}` });
    } finally {
      setIsSaving(false);
      // Clear message after 5 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 5000);
    }
  };

  const nextExample = () => {
    if (currentExample < filteredExamples.length - 1) {
      setCurrentExample(currentExample + 1);
      setCurrentStep(0);
    }
  };

  const prevExample = () => {
    if (currentExample > 0) {
      setCurrentExample(currentExample - 1);
      setCurrentStep(0);
    }
  };

  const getCurrentStepColor = () => stepColors[safeCurrentStep] || stepColors[0];

  const getProgressBarGradient = () => {
    return `linear-gradient(90deg, ${stepColors[0]}, ${getCurrentStepColor()})`;
  };

  // Interactive step selection functions
  const toggleStepSelection = (stepIndex) => {
    const newSelectedSteps = new Set(selectedSteps);
    if (newSelectedSteps.has(stepIndex)) {
      newSelectedSteps.delete(stepIndex);
    } else {
      newSelectedSteps.add(stepIndex);
    }
    setSelectedSteps(newSelectedSteps);
  };

  const toggleInteractiveMode = () => {
    setIsInteractiveMode(!isInteractiveMode);
    if (!isInteractiveMode) {
      // When entering interactive mode, clear selected steps
      setSelectedSteps(new Set());
    }
  };

  const resetInteractiveSelection = () => {
    setSelectedSteps(new Set());
  };

  const getEffectiveCurrentStep = () => {
    if (isInteractiveMode && selectedSteps.size > 0) {
      // In interactive mode, show the highest selected step
      return Math.max(...selectedSteps);
    }
    return safeCurrentStep;
  };

  const getEffectiveSteps = () => {
    if (isInteractiveMode && selectedSteps.size > 0) {
      // In interactive mode, show only selected steps up to the highest selected
      const maxStep = Math.max(...selectedSteps);
      return steps.slice(0, maxStep + 1);
    }
    return steps.slice(0, safeCurrentStep + 1);
  };

  // Extract incremental content from selected steps
  const getSelectedStepsContent = () => {
    if (!isInteractiveMode || selectedSteps.size === 0) {
      return currentStepData.content;
    }

    // Sort selected steps to maintain order
    const sortedSelectedSteps = Array.from(selectedSteps).sort((a, b) => a - b);
    
    // Build the sentence by combining incremental parts
    let result = '';
    
    for (let i = 0; i < sortedSelectedSteps.length; i++) {
      const stepIndex = sortedSelectedSteps[i];
      const step = steps[stepIndex];
      
      if (stepIndex === 0) {
        // First step (topic) - use full content
        result = step.content;
      } else {
        // For other steps, find the incremental part by comparing with the immediate previous step
        const previousStep = steps[stepIndex - 1];
        
        const currentContent = step.content;
        const previousContent = previousStep.content;
        
        // Find the incremental part by comparing with previous step
        if (currentContent.startsWith(previousContent)) {
          const incremental = currentContent.substring(previousContent.length).trim();
          if (incremental) {
            // Add the incremental part to the result
            result += (result ? ' ' : '') + incremental;
          }
        } else {
          // If it doesn't start with previous content, this might be a different structure
          // In this case, we need to find what was added in this step
          // For now, let's use a simpler approach - just use the current step content
          result = currentContent;
        }
      }
    }
    
    return result;
  };

  // Alternative approach: Show the content of the highest selected step
  const getSelectedStepsContentAlternative = () => {
    if (!isInteractiveMode || selectedSteps.size === 0) {
      return currentStepData.content;
    }

    // Sort selected steps to maintain order
    const sortedSelectedSteps = Array.from(selectedSteps).sort((a, b) => a - b);
    
    // For now, let's try a simpler approach: show the content of the highest selected step
    const highestStepIndex = Math.max(...selectedSteps);
    return steps[highestStepIndex].content;
  };


  // AI Drill functionality
  const handleAIGenerate = async (keyword) => {
    setIsGenerating(true);
    setAiProgress(null);
    
    try {
      // Update progress - starting
      setAiProgress({
        status: 'starting',
        message: 'Connecting to A2A backend...',
        timeElapsed: 0,
        tokensUsed: 0,
        costIncurred: 0
      });
      
      // Use A2A service for sentence expansion
      const result = await englishWritingDataService.expandSentence(keyword, {
        context: 'general',
        expansion_type: 'descriptive'
      });
      
      // Update progress - processing
      setAiProgress({
        status: 'processing',
        message: 'Processing expansion...',
        timeElapsed: 1000,
        tokensUsed: 0,
        costIncurred: 0
      });
      
      // Transform A2A result to match expected format
      const expandedSentences = result.expandedSentences || result.data?.expandedSentences || [];
      const aiData = {
        topic: keyword,
        steps: expandedSentences.map(step => ({
          label: step.label || '',
          content: step.content || ''
        }))
      };
      
      // Convert the AI-generated data to match our format
      const convertedData = convertExampleData(aiData, selectedLanguage);
      
      // Update progress - completed
      setAiProgress({
        status: 'completed',
        message: 'Expansion completed successfully',
        timeElapsed: 2000,
        tokensUsed: 0,
        costIncurred: 0
      });
      
      if (convertedData && convertedData.steps && convertedData.steps.length > 0) {
        // Store the AI-generated example data for saving
        const aiExample = {
          ...convertedData,
          category: 'UserDefined' // Will be saved with this category
        };
        setCurrentAIGeneratedExample(aiExample);
        setSaveMessage(null); // Clear any previous save message
        
        // Add to filtered examples and switch to it
        const newFilteredExamples = [...filteredExamples, {
          ...convertedData,
          category: 'AI Generated' // Display category
        }];
        setFilteredExamples(newFilteredExamples);
        setCurrentExample(newFilteredExamples.length - 1);
        setCurrentStep(0);
      } else {
        console.error('AI generation failed: No valid steps returned');
        setCurrentAIGeneratedExample(null);
        setSaveMessage({
          type: 'error',
          text: 'Failed to generate expansion. Please try again.'
        });
      }
    } catch (error) {
      console.error('AI generation error:', error);
    } finally {
      setIsGenerating(false);
      // Clear progress after a delay
      setTimeout(() => {
        setAiProgress(null);
      }, 3000);
    }
  };


  // Show loading state
  if (isLoading || !languageData) {
    return (
      <div className="sentence-expansion-app">
        <header className="app-header">
          <h1>Sentence Expansion - Writing Practice</h1>
          <p>Start with simple words and expand them step by step into rich paragraphs</p>
        </header>
        <div className="main-content">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h3>{currentLanguageData.loading || 'Loading examples...'}</h3>
            {loadingError && (
              <div style={{ color: 'red', marginTop: '20px' }}>
                <p>{loadingError}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no examples are available
  if (filteredExamples.length === 0 && !isLoading) {
    return (
      <div className="sentence-expansion-app">
        <header className="app-header">
          <h1>{currentLanguageData.appTitle}</h1>
          <p>{currentLanguageData.appSubtitle}</p>
        </header>
        <div className="main-content">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h3>No examples available</h3>
            {loadingError && (
              <div style={{ color: 'red', marginTop: '20px' }}>
                <p>{loadingError}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Handle starting a new composition
  const handleNewComposition = () => {
    // Clear all composition-related state
    setCurrentComposition(null);
    setOriginalCompositionData(null);
    setCompositionProgress(null);
    setIsGeneratingComposition(false);
  };

  // Handle loading a saved composition (from dropdown)
  const handleLoadSavedComposition = (compositionData) => {
    if (!compositionData) {
      // Clear composition display when dropdown is cleared
      setCurrentComposition(null);
      setOriginalCompositionData(null);
      return;
    }

    console.log('Loading saved composition for display:', compositionData);
    // Set the current composition state to display the loaded composition
    setCurrentComposition(compositionData);
    
    // Also store the original composition data structure for saving
    // Reconstruct the original composition data from the loaded composition
    if (compositionData.nodes && compositionData.nodes.length > 0) {
      const originalData = {
        title: compositionData.title,
        wordLength: compositionData.targetWordLength || 300,
        style: compositionData.style || 'narrative',
        nodes: compositionData.nodes.map(node => ({
          name: node.name,
          expandedSteps: node.steps || []
        }))
      };
      setOriginalCompositionData(originalData);
    }
  };

  // Handle composition generation
  const handleCompositionGenerate = async (compositionData) => {
    setIsGeneratingComposition(true);
    setCompositionProgress(null);
    setOriginalCompositionData(null); // Clear previous data
    
    try {
      // Store original composition data for saving later
      setOriginalCompositionData(compositionData);
      
      const result = await generateComposition(
        compositionData, 
        (progress) => {
          setCompositionProgress(progress);
        }
      );
      
      if (result.success) {
        console.log('Composition generation result:', result.data);
        // Normalize the composition data to ensure consistent property names
        const compositionData = result.data || {};
        const normalizedComposition = {
          title: compositionData.title || originalCompositionData?.title || 'Untitled',
          style: compositionData.style || originalCompositionData?.style || 'narrative',
          targetWordLength: compositionData.targetWordLength || compositionData.target_word_length || originalCompositionData?.wordLength || 300,
          nodes: compositionData.nodes || [],
          finalComposition: compositionData.finalComposition || compositionData.final_composition || compositionData.composition || ''
        };
        
        console.log('Normalized composition:', normalizedComposition);
        setCurrentComposition(normalizedComposition);
        // Keep in composition mode to display results
        // User can switch modes manually if needed
      } else {
        console.error('Composition generation failed:', result.error);
        alert(`Composition generation failed: ${result.error}`);
        setOriginalCompositionData(null); // Clear on failure
      }
    } catch (error) {
      console.error('Composition generation error:', error);
      alert(`Error: ${error.message}`);
      setOriginalCompositionData(null); // Clear on error
    } finally {
      setIsGeneratingComposition(false);
      setTimeout(() => {
        setCompositionProgress(null);
      }, 3000);
    }
  };

  // Handle refining composition
  const handleRefineComposition = async () => {
    if (!currentComposition || !currentComposition.finalComposition) {
      alert('No composition available to refine');
      return;
    }

    setIsRefining(true);
    setRefinementProgress(null);
    setRefinedComposition(null);

    try {
      const result = await refineComposition(
        currentComposition.finalComposition,
        refinementLevel,
        refinementStage,
        (progress) => {
          setRefinementProgress(progress);
        }
      );

      if (result.success) {
        setRefinedComposition(result.refinedText);
      } else {
        console.error('Refinement failed:', result.error);
        alert(`Refinement failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Refinement error:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsRefining(false);
      setTimeout(() => {
        setRefinementProgress(null);
      }, 3000);
    }
  };

  // Handle saving composition to Supabase
  const handleSaveComposition = async () => {
    if (!currentComposition || !originalCompositionData) {
      alert('No composition data available to save');
      return;
    }

    setIsSavingComposition(true);
    setCompositionSaveMessage(null);

    try {
      // Prepare nodes data with selected sentences and their step labels
      const nodesForSave = originalCompositionData.nodes.map(node => {
        // Get the selected sentences with their step labels
        const selectedSentences = (node.expandedSteps || []).map(step => ({
          step_label: step.label || step.step_label || '',
          sentence_content: step.content || step.sentence_content || ''
        }));

        return {
          name: node.name,
          selectedSentences: selectedSentences
        };
      });

      const saveData = {
        title: currentComposition.title || originalCompositionData.title,
        style: currentComposition.style || originalCompositionData.style,
        wordLength: currentComposition.targetWordLength || originalCompositionData.wordLength,
        finalCompositionText: currentComposition.finalComposition || '',
        nodes: nodesForSave
      };

      const saveResult = await englishWritingDataService.saveComposition({
        title: saveData.title,
        content: saveData.finalCompositionText,
        style: saveData.style,
        word_length: saveData.wordLength,
        nodes_data: saveData.nodes.map(node => ({
          node_name: node.name,
          selected_sentences: node.selectedSentences.map(s => ({
            step_label: s.step_label || s.label,
            sentence_content: s.sentence_content || s.content
          }))
        })),
        category: 'AIComposition'
      });
      
      // Transform result to match expected format
      const result = {
        success: saveResult.success !== false,
        message: saveResult.message || 'Composition saved successfully!',
        compositionId: saveResult.id || saveResult.composition?.id || saveResult.compositionId || null
      };

      if (result.success) {
        setCompositionSaveMessage({
          type: 'success',
          text: result.message || 'Composition saved successfully!'
        });
        // Clear message after 5 seconds
        setTimeout(() => {
          setCompositionSaveMessage(null);
        }, 5000);
      }
    } catch (error) {
      console.error('Error saving composition:', error);
      setCompositionSaveMessage({
        type: 'error',
        text: `Failed to save composition: ${error.message}`
      });
      // Clear error message after 5 seconds
      setTimeout(() => {
        setCompositionSaveMessage(null);
      }, 5000);
    } finally {
      setIsSavingComposition(false);
    }
  };

  return (
    <div className="sentence-expansion-app">
      <header className="app-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <h1>{currentLanguageData.appTitle}</h1>
            <p>{currentLanguageData.appSubtitle}</p>
          </div>
          <ConnectionStatusIndicator />
        </div>
        
        {/* Mode Switcher */}
        <div className="mode-switcher" style={{ marginTop: '15px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button
            onClick={() => setMode('sentence')}
            className={`mode-button ${mode === 'sentence' ? 'active' : ''}`}
            style={{
              padding: '8px 20px',
              backgroundColor: mode === 'sentence' ? '#2196F3' : '#e0e0e0',
              color: mode === 'sentence' ? 'white' : '#666',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            📝 Sentence Expansion
          </button>
          <button
            onClick={() => setMode('composition')}
            className={`mode-button ${mode === 'composition' ? 'active' : ''}`}
            style={{
              padding: '8px 20px',
              backgroundColor: mode === 'composition' ? '#2196F3' : '#e0e0e0',
              color: mode === 'composition' ? 'white' : '#666',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            🎨 Composition Expansion
          </button>
        </div>
      </header>

      <div className="main-content">
        {/* Show Composition Composer when in composition mode */}
        {mode === 'composition' ? (
          <>
            <CompositionComposer
              onGenerateComposition={handleCompositionGenerate}
              isGenerating={isGeneratingComposition}
              stepColors={stepColors}
              selectedLanguage={selectedLanguage}
              onLoadComposition={handleLoadSavedComposition}
              onNewComposition={handleNewComposition}
            />
            
            {/* Composition Progress */}
            <AIDrillProgress 
              progress={compositionProgress}
              isVisible={isGeneratingComposition || (compositionProgress && compositionProgress.status !== 'completed')}
            />
            
            {/* Display Generated Composition - Only show if we have a composition with finalComposition text */}
            {currentComposition && currentComposition.finalComposition && (
              <div className="composition-result" style={{ 
                marginTop: '30px', 
                padding: '20px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px',
                border: '2px solid #28a745'
              }}>
                <h2 style={{ color: '#28a745', marginBottom: '15px' }}>✨ Generated Composition: {currentComposition.title || 'Untitled'}</h2>
                
                {/* Show nodes */}
                {currentComposition.nodes && currentComposition.nodes.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    {currentComposition.nodes.map((node, nodeIndex) => (
                      <div key={nodeIndex} style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '6px' }}>
                        <h3 style={{ color: '#333', marginBottom: '10px' }}>Node {nodeIndex + 1}: {node.name || 'Unnamed'}</h3>
                        {node.steps && node.steps.length > 0 && node.steps.map((step, stepIndex) => {
                          const stepColorIndex = step.index !== undefined ? step.index : stepIndex;
                          return (
                            <div key={stepIndex} style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                              <strong style={{ color: stepColors[stepColorIndex] || '#333' }}>{step.label || 'Step'}:</strong> {step.content || ''}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Refinement Controls */}
                {currentComposition && currentComposition.finalComposition && (
                  <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff9e6', borderRadius: '6px', border: '1px solid #ffd700' }}>
                    <h3 style={{ color: '#856404', marginBottom: '15px' }}>🔧 Refine Composition</h3>
                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>
                          {currentLanguageData.refinement?.selectLevel || 'Select Writing Level:'}
                        </label>
                        <select
                          value={refinementLevel}
                          onChange={(e) => {
                            setRefinementLevel(e.target.value);
                            // Set default stage for the selected level
                            if (e.target.value === 'primary') {
                              setRefinementStage('year5-6');
                            } else if (e.target.value === 'secondary') {
                              setRefinementStage('junior');
                            } else {
                              setRefinementStage('general');
                            }
                          }}
                          style={{
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px',
                            minWidth: '150px'
                          }}
                        >
                          <option value="primary">{currentLanguageData.refinement?.levels?.primary || 'Primary Student'}</option>
                          <option value="secondary">{currentLanguageData.refinement?.levels?.secondary || 'Secondary Student'}</option>
                          <option value="university">{currentLanguageData.refinement?.levels?.university || 'University Student'}</option>
                          <option value="adult">{currentLanguageData.refinement?.levels?.adult || 'Adult Student'}</option>
                        </select>
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>
                          {currentLanguageData.refinement?.selectStage || 'Select Stage:'}
                        </label>
                        <select
                          value={refinementStage}
                          onChange={(e) => setRefinementStage(e.target.value)}
                          style={{
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px',
                            minWidth: '170px'
                          }}
                        >
                          {refinementLevel === 'primary' && (
                            <>
                              <option value="year1-2">{currentLanguageData.refinement?.stages?.primary?.['year1-2'] || 'Year 1-2'}</option>
                              <option value="year3-4">{currentLanguageData.refinement?.stages?.primary?.['year3-4'] || 'Year 3-4'}</option>
                              <option value="year5-6">{currentLanguageData.refinement?.stages?.primary?.['year5-6'] || 'Year 5-6'}</option>
                            </>
                          )}
                          {refinementLevel === 'secondary' && (
                            <>
                              <option value="junior">{currentLanguageData.refinement?.stages?.secondary?.junior || 'Junior Secondary'}</option>
                              <option value="senior">{currentLanguageData.refinement?.stages?.secondary?.senior || 'Senior Secondary'}</option>
                            </>
                          )}
                          {refinementLevel === 'university' && (
                            <option value="general">{currentLanguageData.refinement?.stages?.university?.general || 'University Level'}</option>
                          )}
                          {refinementLevel === 'adult' && (
                            <option value="general">{currentLanguageData.refinement?.stages?.adult?.general || 'Adult Level'}</option>
                          )}
                        </select>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button
                          onClick={handleRefineComposition}
                          disabled={isRefining}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: isRefining ? '#6c757d' : '#ff9800',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isRefining ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {isRefining ? '⏳ Refining...' : '✨ ' + (currentLanguageData.refinement?.refineButton || 'Refine Composition')}
                        </button>
                      </div>
                    </div>
                    
                    {/* Refinement Progress */}
                    {isRefining && refinementProgress && (
                      <AIDrillProgress 
                        progress={refinementProgress}
                        isVisible={isRefining || (refinementProgress && refinementProgress.status !== 'completed')}
                      />
                    )}
                  </div>
                )}

                {/* Final Composition - Show even if empty to help debug */}
                <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e8f5e9', borderRadius: '6px' }}>
                  <h3 style={{ color: '#2e7d32', marginBottom: '10px' }}>
                    {refinedComposition ? '✨ Refined Composition:' : 'Complete Composition:'}
                  </h3>
                  {(refinedComposition || currentComposition.finalComposition) ? (
                    <>
                      <p style={{ lineHeight: '1.8', fontSize: '15px', color: '#333', whiteSpace: 'pre-wrap' }}>
                        {refinedComposition || currentComposition.finalComposition}
                      </p>
                      
                      {/* Voice Controls for Composition */}
                      <VoiceControls 
                        text={refinedComposition || currentComposition.finalComposition}
                        isVisible={!!(refinedComposition || currentComposition.finalComposition)}
                      />
                      
                      {/* Show original if refined */}
                      {refinedComposition && (
                        <details style={{ marginTop: '15px', padding: '10px', backgroundColor: 'rgba(255,255,255,0.5)', borderRadius: '4px' }}>
                          <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#2e7d32' }}>
                            View Original Composition
                          </summary>
                          <p style={{ marginTop: '10px', lineHeight: '1.8', fontSize: '14px', color: '#666', whiteSpace: 'pre-wrap' }}>
                            {currentComposition.finalComposition}
                          </p>
                        </details>
                      )}
                    </>
                  ) : (
                    <div style={{ padding: '15px', backgroundColor: '#fff3cd', borderRadius: '4px', color: '#856404' }}>
                      <p style={{ margin: 0, fontWeight: 'bold' }}>⚠️ No composition text received from AI.</p>
                      <p style={{ margin: '8px 0 0 0', fontSize: '13px' }}>
                        The AI response may not have included the finalComposition field. Check the browser console for details.
                      </p>
                      <details style={{ marginTop: '10px', fontSize: '12px' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>View raw composition data</summary>
                        <pre style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', overflow: 'auto', maxHeight: '200px' }}>
                          {JSON.stringify(currentComposition, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>

                {/* Save Button */}
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <button
                    onClick={handleSaveComposition}
                    disabled={isSavingComposition || !originalCompositionData}
                    style={{
                      padding: '12px 30px',
                      backgroundColor: isSavingComposition ? '#6c757d' : '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: (isSavingComposition || !originalCompositionData) ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      minWidth: '200px'
                    }}
                  >
                    {isSavingComposition ? '💾 Saving...' : '💾 Save Composition to Database'}
                  </button>
                  
                  {compositionSaveMessage && (
                    <div
                      style={{
                        marginTop: '15px',
                        padding: '12px',
                        borderRadius: '6px',
                        backgroundColor: compositionSaveMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                        color: compositionSaveMessage.type === 'success' ? '#155724' : '#721c24',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                    >
                      {compositionSaveMessage.text}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <>

        {/* AI Drill Input */}
        <AIDrillInput 
          onGenerate={handleAIGenerate}
          isGenerating={isGenerating}
          selectedLanguage={selectedLanguage}
        />

        {/* AI Drill Progress */}
        <AIDrillProgress 
          progress={aiProgress}
          isVisible={isGenerating || (aiProgress && aiProgress.status !== 'completed')}
        />

        {/* Category selector */}
        <div className="category-selector">
          <h3>{currentLanguageData.selectCategory}</h3>
          <div className="category-buttons">
            {categories.map((category) => (
              <button
                key={category.name}
                onClick={() => setSelectedCategory(category.name)}
                className={`category-button ${selectedCategory === category.name ? 'active' : ''}`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Example selector */}
        <div className="example-selector">
          <h3>
            {currentLanguageData.example} {safeCurrentExample + 1} / {filteredExamples.length}: {currentExampleData.topic}
            {currentExampleData.category && <span className="category-tag"> ({currentExampleData.category})</span>}
          </h3>
          <div className="example-nav-buttons">
            <button 
              onClick={prevExample} 
              disabled={safeCurrentExample === 0}
              className={`example-nav-button prev ${safeCurrentExample === 0 ? 'disabled' : ''}`}
            >
              {currentLanguageData.previousExample}
            </button>
            <button 
              onClick={nextExample} 
              disabled={safeCurrentExample === filteredExamples.length - 1}
              className={`example-nav-button next ${safeCurrentExample === filteredExamples.length - 1 ? 'disabled' : ''}`}
            >
              {currentLanguageData.nextExample}
            </button>
          </div>
          
          {/* Save button for AI-generated examples */}
          {currentExampleData.category === 'AI Generated' && currentAIGeneratedExample && (
            <div className="save-example-container" style={{ marginTop: '10px', textAlign: 'center' }}>
              <button
                onClick={handleSaveExample}
                disabled={isSaving || currentExampleData.saved}
                className="save-button"
                style={{
                  padding: '10px 20px',
                  backgroundColor: currentExampleData.saved ? '#4CAF50' : '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: isSaving || currentExampleData.saved ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {isSaving ? 'Saving...' : currentExampleData.saved ? '✓ Saved' : '💾 Save to Database'}
              </button>
              {saveMessage && (
                <div 
                  style={{
                    marginTop: '10px',
                    padding: '8px',
                    borderRadius: '4px',
                    backgroundColor: saveMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                    color: saveMessage.type === 'success' ? '#155724' : '#721c24',
                    fontSize: '12px'
                  }}
                >
                  {saveMessage.text}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Interactive Mode Toggle */}
        <div className="interactive-mode-toggle">
          <button
            onClick={toggleInteractiveMode}
            className={`interactive-toggle-button ${isInteractiveMode ? 'active' : ''}`}
          >
            {isInteractiveMode ? '🎯 Interactive Mode' : '🎯 Toggle Interactive Mode'}
          </button>
          {isInteractiveMode && (
            <button
              onClick={resetInteractiveSelection}
              className="reset-selection-button"
            >
              Reset Selection
            </button>
          )}
        </div>

        {/* Progress visualization with 15 color-coded steps */}
        <div className="progress-visualization">
          <h4>
            {currentLanguageData.expansionProgress}
            {isInteractiveMode && (
              <span className="interactive-mode-indicator">
                (Interactive Mode: {selectedSteps.size} steps selected)
              </span>
            )}
          </h4>
          <div className="progress-steps">
            {steps.map((step, index) => {
              const isSelected = selectedSteps.has(index);
              const isCompleted = isInteractiveMode ? isSelected : index <= safeCurrentStep;
              const isClickable = isInteractiveMode;
              
              return (
                <button
                  key={index}
                  onClick={isClickable ? () => toggleStepSelection(index) : undefined}
                  className={`progress-step ${isCompleted ? 'completed' : 'pending'} ${isClickable ? 'clickable' : ''} ${isSelected ? 'selected' : ''}`}
                  style={{
                    backgroundColor: stepColors[index] || stepColors[0],
                    cursor: isClickable ? 'pointer' : 'default'
                  }}
                  disabled={!isClickable}
                >
                  {step.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="step-display">
          <div className="step-indicator">
            <span 
              className="step-number"
              style={{ backgroundColor: getCurrentStepColor() }}
            >
              {getEffectiveCurrentStep() + 1}
            </span> / {steps.length}
            {isInteractiveMode && selectedSteps.size > 0 && (
              <span className="interactive-indicator">
                (Selected: {selectedSteps.size})
              </span>
            )}
          </div>

          <div className="step-content">
            <div 
              className="step-label"
              style={{ backgroundColor: getCurrentStepColor() }}
            >
              {isInteractiveMode && selectedSteps.size > 0 
                ? `Interactive Mode - ${selectedSteps.size} steps selected`
                : currentStepData.label
              }
            </div>
            <div 
              className="step-text"
              style={{
                borderColor: getCurrentStepColor(),
                backgroundColor: `${getCurrentStepColor()}15`
              }}
            >
              {getSelectedStepsContentAlternative()}
            </div>
            
            {/* Voice Controls */}
            <VoiceControls 
              text={getSelectedStepsContentAlternative()}
              isVisible={!!getSelectedStepsContentAlternative()}
            />
            
            {/* Phonetic Display */}
            <PhoneticDisplay 
              text={getSelectedStepsContentAlternative()}
              isVisible={!!getSelectedStepsContentAlternative()}
            />
          </div>

          <div className="control-buttons">
            {!isInteractiveMode ? (
              <>
                <button 
                  onClick={prevStep} 
                  disabled={safeCurrentStep === 0}
                  className="control-button prev"
                >
                  {currentLanguageData.previousStep}
                </button>
                <button 
                  onClick={resetSteps} 
                  className="control-button reset"
                >
                  {currentLanguageData.resetSteps}
                </button>
                <button 
                  onClick={nextStep} 
                  disabled={safeCurrentStep === steps.length - 1}
                  className="control-button next"
                >
                  {currentLanguageData.nextStep}
                </button>
              </>
            ) : (
              <>
                <button 
                  onClick={resetInteractiveSelection} 
                  className="control-button reset"
                >
                  Reset Selection
                </button>
                <button 
                  onClick={toggleInteractiveMode} 
                  className="control-button exit-interactive"
                >
                  Exit Interactive Mode
                </button>
              </>
            )}
          </div>
        </div>

        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{
              background: getProgressBarGradient(),
              width: `${((getEffectiveCurrentStep() + 1) / steps.length) * 100}%`
            }}
          />
        </div>

        <div className="step-reference">
          <h3>{currentLanguageData.stepExplanation}</h3>
          
          {/* Voice control for step reference */}
          <VoiceControls 
            text={steps.map(step => step.label).join('、')}
            isVisible={steps.length > 0}
          />
          
          <div className="step-grid">
            {steps.map((step, index) => (
              <div
                key={index}
                className="step-item"
                style={{
                  backgroundColor: `${stepColors[index]}15`,
                  borderColor: `${stepColors[index]}50`
                }}
              >
                <div
                  className="step-circle"
                  style={{ backgroundColor: stepColors[index] }}
                >
                  {index + 1}
                </div>
                <span className="step-name">{step.label}</span>
              </div>
            ))}
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SentenceExpansionApp;